import React, { useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import { useIdentity } from '@internal/shared';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import {
  INVITATION_ACCEPTANCE_ERROR_MESSAGE,
  INVITATION_REVOKED_MESSAGE,
  TEMPORARY_INVITATION_TEAM_NAME,
} from './constants';
import {
  InvitationAcceptanceErrorIcon,
  InvitationAcceptedIcon,
  InvitationTeamName,
} from './styles';
import { InvitationAcceptancePageProps } from './types';
import { useAcceptInvitation } from './useAcceptInvitation';
import { InvitationAcceptanceStatusView } from './InvitationAcceptanceStatusView';
import { isAcceptanceTokenRevoked } from '../../invitationUrlUtils';

function readAcceptanceToken(search: string): string | null {
  const acceptanceToken = new URLSearchParams(search).get('token')?.trim();
  return acceptanceToken || null;
}

export function InvitationAcceptancePage({
  onLoginClick,
}: InvitationAcceptancePageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { idToken } = useIdentity();
  const acceptanceToken = useMemo(
    () => readAcceptanceToken(location.search),
    [location.search],
  );
  const invitationAcceptance = useAcceptInvitation(acceptanceToken);
  const isAuthenticated = Boolean(idToken);

  function openPortalOrLogin() {
    if (isAuthenticated || !onLoginClick) {
      navigate('/');
      return;
    }

    onLoginClick();
  }

  const portalPrimaryAction = {
    label: isAuthenticated ? 'Go to portal' : 'Log in',
    onClick: openPortalOrLogin,
  };

  if (!acceptanceToken) {
    return (
      <InvitationAcceptanceStatusView
        acceptanceIcon={
          <InvitationAcceptanceErrorIcon aria-label="Invitation link error" />
        }
        acceptanceMessage="The invitation token is missing. Request a new invitation from your team administrator."
        acceptanceTitle="Invitation link is invalid"
      />
    );
  }

  if (isAcceptanceTokenRevoked(acceptanceToken)) {
    return (
      <InvitationAcceptanceStatusView
        acceptanceIcon={
          <InvitationAcceptanceErrorIcon aria-label="Invitation revoked" />
        }
        acceptanceMessage={INVITATION_REVOKED_MESSAGE}
        acceptanceTitle="Invitation could not be accepted"
        primaryAction={portalPrimaryAction}
      />
    );
  }

  if (invitationAcceptance.isPending) {
    return (
      <InvitationAcceptanceStatusView
        acceptanceIcon={
          <CircularProgress aria-label="Accepting invitation" size={48} />
        }
        acceptanceMessage="Accepting your invitation..."
        announceAsStatus
      />
    );
  }

  if (invitationAcceptance.isError) {
    const acceptanceErrorMessage = getApiErrorMessage(
      invitationAcceptance.error,
      INVITATION_ACCEPTANCE_ERROR_MESSAGE,
    );

    return (
      <InvitationAcceptanceStatusView
        acceptanceIcon={
          <InvitationAcceptanceErrorIcon aria-label="Invitation acceptance error" />
        }
        acceptanceMessage={acceptanceErrorMessage}
        acceptanceTitle="Invitation could not be accepted"
        primaryAction={portalPrimaryAction}
      />
    );
  }

  return (
    <InvitationAcceptanceStatusView
      acceptanceIcon={
        <InvitationAcceptedIcon aria-label="Invitation accepted" />
      }
      acceptanceMessage={
        <>
          You have successfully accepted an invitation to the{' '}
          <InvitationTeamName>
            {TEMPORARY_INVITATION_TEAM_NAME}
          </InvitationTeamName>{' '}
          team
        </>
      }
      acceptanceTitle="Invitation Accepted"
      primaryAction={portalPrimaryAction}
    />
  );
}
